import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AppState } from 'src/app/store/reducers';

import { AppSessionService } from '../app-session/app-session.service';

import { PaginationRequest } from 'src/app/models/common/paginationRequest';
import { FilterOperation } from 'src/app/models/custom-table/custom-table.model';
import { setSimpleFilterQuery, setAdvancedFilterQuery } from '../../utils/tableUtils';
import { GridUtils } from '../../utils/gridUtils';

const EMPTY_STRING = '';

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {

  private BASE_URL = this.appSession.baseUrl + '/api/v1/vehicles';

  constructor(private http: HttpClient, private store: Store<AppState>, private appSession: AppSessionService) { }

  getBrands(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/brands`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + (pageReq.limit ? '&limit=' + pageReq.limit : '') + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  getModels(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/models`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + (pageReq.limit ? '&limit=' + pageReq.limit : '') + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  getVersions(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/versions`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + (pageReq.limit ? '&limit=' + pageReq.limit : '') + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  getMotorizations(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/motorizations`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + (pageReq.limit ? '&limit=' + pageReq.limit : '') + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  getOdometerTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/odometerTypes`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + (pageReq.limit ? '&limit=' + pageReq.limit : '') + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  getConsumptionRules(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/consumptionRules`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: undefined,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + (pageReq.limit ? '&limit=' + pageReq.limit : '') + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  // create vehicle
  createVehicle(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.vehicle);
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}`;

    return this.http.post(url, data, { headers });
  }

  // update vehicle
  updateVehicle(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.vehicle);
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/${action.vehicle.code}`;

    return this.http.put(url, data, { headers });
  }

  // save vehicle tanks
  saveVehicleTanks(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.vehicleTankCodes);
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/` + encodeURIComponent(action.vehicleCode) + `/tanks`;

    return this.http.put(url, data, { headers });
  }

  // delete vehicle tanks
  deleteVehicleTanks(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let data = '';

    if (action.vehicleTankCodes.length > 0) {
      action.vehicleTankCodes.forEach(vehicleTankCode => {
        if (data.length === 0) {
          data = data.concat('?vehicleTankCode=' + vehicleTankCode);
        } else {
          data = data.concat('&vehicleTankCode=' + vehicleTankCode);
        }
      });
    }
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/` + encodeURIComponent(action.vehicleCode) + `/tanks` + data;

    return this.http.request('delete', url, { headers });
  }


  // save vehicle ODO's
  saveVehicleODOs(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.vehicleODOsCodes);
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/` + encodeURIComponent(action.vehicleCode) + `/odometers`;

    return this.http.put(url, data, { headers });
  }

  // delete vehicle ODO's
  deleteVehicleODOs(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let data = '';

    if (action.vehicleODOsCodes.length > 0) {
      action.vehicleODOsCodes.forEach(vehicleODOsCode => {
        if (data.length === 0) {
          data = data.concat('?vehicleOdometerCode=' + vehicleODOsCode);
        } else {
          data = data.concat('&vehicleOdometerCode=' + vehicleODOsCode);
        }
      });
    }
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/` + encodeURIComponent(action.vehicleCode) + `/odometers` + data;
    return this.http.request('delete', url, { headers });
  }

  // save vehicle Averages
  saveVehicleAverages(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.vehicleAveragesCodes);
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/` + encodeURIComponent(action.vehicleCode) + `/averages`;

    return this.http.put(url, data, { headers });
  }

  // delete vehicle Averages
  deleteVehicleAverages(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let data = '';

    if (action.vehicleAveragesCodes.length > 0) {
      action.vehicleAveragesCodes.forEach(vehicleAveragesCode => {
        if (data.length === 0) {
          data = data.concat('?vehicleAverageCode=' + vehicleAveragesCode);
        } else {
          data = data.concat('&vehicleAverageCode=' + vehicleAveragesCode);
        }
      });
    }
    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/` + encodeURIComponent(action.vehicleCode) + `/averages` + data;

    return this.http.request('delete', url, { headers });
  }

  // get Vehicle
  getVehicle(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    const url = `${this.appSession.baseUrl + '/api/v1/vehicles'}/${action.vehicleCode}`;

    return this.http.get(url, { headers });
  }

  // Get Vehicles
  public getVehiclesList(paging?: any, filteringArgs?: any, sortingArgs?: any, advancedFilteringArgs?: any, container?:Map<string, string> ): Observable<any> {
    let headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append('sort', orderQuery);
    }

    return this.http.get(this.buildDataUrl(paging, '/search?'), { headers });
  }

  // Grid filter helpers block
  private buildDataUrl(pagingArgs: any, path: string): string {
    let baseQueryString = `${this.appSession.baseUrl + '/api/v1/vehicles'}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query += (scrollingQuery !== EMPTY_STRING) ? `&${scrollingQuery}` : EMPTY_STRING;
    baseQueryString += query;

    return baseQueryString;
  }
}
